/**
 * iter88 — Shared lead-source bucketing logic for the Dashboard and
 * Customer Leads charts.
 *
 * Previously the Dashboard read `utm_source || referrer` and the
 * Customer Leads page additionally folded in `lead.source` — but
 * `lead.source` stores the lead TYPE ("unlock" / "trade-in" / etc.)
 * not the marketing channel, so it polluted the Customer Leads
 * chart with bogus "Other" attributions and made the two pages
 * disagree.
 *
 * Per Issue 2 the canonical contract is:
 *   1. Use utm_source first (paid / tagged campaigns)
 *   2. Then fbclid / gclid (click-ID attribution)
 *   3. Then the referrer host
 *   4. Anything we can't bucket → "Unknown" (NOT "Direct")
 */

export const SOURCE_ORDER = [
  { key: "Direct", color: "#4C1D95" },
  { key: "Facebook", color: "#1D4ED8" },
  { key: "Cars.com", color: "#6D28D9" },
  { key: "TrueCar", color: "#2563EB" },
  { key: "AutoTrader", color: "#7C3AED" },
  { key: "CarGurus", color: "#3B82F6" },
  { key: "Google", color: "#8B5CF6" },
  { key: "Unknown", color: "#6B7280" },
];

const KEYS = new Set(SOURCE_ORDER.map((s) => s.key));

/**
 * Normalise a raw source string (from utm_source or referrer host)
 * into one of our canonical buckets. Returns "Unknown" when the raw
 * value is empty/unrecognised — per Issue 2 we no longer silently
 * absorb null values into "Direct".
 */
export function normalizeSource(raw) {
  if (!raw) return "Unknown";
  const n = String(raw).toLowerCase();
  if (n.includes("direct")) return "Direct";
  if (n.includes("facebook") || n.includes("fbclid") || n === "fb") return "Facebook";
  if (n.includes("cars.com") || n === "cars") return "Cars.com";
  if (n.includes("truecar")) return "TrueCar";
  if (n.includes("autotrader") || n.includes("auto trader")) return "AutoTrader";
  if (n.includes("cargurus")) return "CarGurus";
  if (n.includes("google") || n.includes("gclid")) return "Google";
  // Unknown but truthy raw values still fall through to Unknown so the
  // chart never shows "unlock"/"trade-in" labels.
  return KEYS.has(raw) ? raw : "Unknown";
}

/**
 * Pull the best-available raw source signal off a lead row. Order:
 *   utm_source → fbclid → gclid → referrer host → null.
 * We deliberately ignore `lead.source` (lead type) and `lead.lead_type`.
 */
export function rawSourceFromLead(lead) {
  if (!lead) return null;
  if (lead.utm_source) return lead.utm_source;
  if (lead.fbclid) return "facebook";
  if (lead.gclid) return "google";
  if (lead.referrer) {
    try {
      const u = new URL(lead.referrer);
      return u.hostname.replace(/^www\./, "");
    } catch {
      return lead.referrer;
    }
  }
  return null;
}

/**
 * Build the chart data for both pages. Returns an array in
 * SOURCE_ORDER order, with `value` = count of leads in each bucket.
 * Caller decides whether to filter out zero buckets.
 */
export function bucketLeadsBySource(leads) {
  const counts = new Map(SOURCE_ORDER.map((s) => [s.key, 0]));
  (leads || []).forEach((l) => {
    const bucket = normalizeSource(rawSourceFromLead(l));
    counts.set(bucket, (counts.get(bucket) || 0) + 1);
  });
  return SOURCE_ORDER.map((s) => ({
    name: s.key,
    value: counts.get(s.key) || 0,
    color: s.color,
  }));
}
